import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  FileJson,
  FileSpreadsheet,
  RefreshCw,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import {
  useParticipantDiagnosticConfig,
  useParticipantDiagnosticLogs,
  useUpdateParticipantDiagnosticConfig,
} from "@/api/hooks";
import {
  downloadParticipantDiagnosticLogs,
} from "@/api/client";
import type {
  DiagnosticCategory,
  DiagnosticConfig,
  DiagnosticDeviceAcknowledgement,
  DiagnosticDurationHours,
  DiagnosticLevel,
  DiagnosticLog,
  DiagnosticLogFilters,
  Device,
} from "@/api/types";
import { buildDeviceLabelMap } from "@/lib/device-labels";
import { formatDate, relativeTime } from "@/lib/format-date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetBody, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, ErrorDisplay, Spinner } from "@/components/loading";

const PAGE_SIZE = 50;
const DURATIONS: Array<{ value: DiagnosticDurationHours; label: string }> = [
  { value: 24, label: "24 hours" },
  { value: 48, label: "48 hours" },
  { value: 168, label: "7 days" },
];
const CATEGORIES: DiagnosticCategory[] = [
  "APP",
  "CONFIG",
  "ALARM",
  "NOTIFICATION",
  "SERVICE",
  "SYNC",
  "NETWORK",
  "POWER",
  "PERMISSION",
  "FCM",
];
const LEVELS: DiagnosticLevel[] = ["DEBUG", "INFO", "WARN", "ERROR"];

interface ParticipantDiagnosticsProps {
  userId: string;
  devices: Device[];
  active?: boolean;
}

type FilterState = Omit<DiagnosticLogFilters, "cursor" | "limit">;

const EMPTY_FILTERS: FilterState = {};

export function ParticipantDiagnostics({ userId, devices, active = true }: ParticipantDiagnosticsProps) {
  const config = useParticipantDiagnosticConfig(userId, active);
  const updateConfig = useUpdateParticipantDiagnosticConfig(userId);
  const [durationOverride, setDurationOverride] = useState<DiagnosticDurationHours | null>(null);
  const [enabledOverride, setEnabledOverride] = useState<boolean | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [pageCursors, setPageCursors] = useState<Array<string | undefined>>([undefined]);
  const [selectedLog, setSelectedLog] = useState<DiagnosticLog | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<"json" | "csv" | null>(null);
  const [eventNameDraft, setEventNameDraft] = useState("");

  const currentConfig = config.data?.data;
  const duration = durationOverride ?? (currentConfig ? inferDuration(currentConfig) : 48);
  const enabledDraft = enabledOverride ?? currentConfig?.enabled ?? false;
  const currentPage = pageCursors.length - 1;
  const currentCursor = pageCursors[currentPage];
  const logFilters = useMemo(
    () => ({ ...filters, cursor: currentCursor, limit: PAGE_SIZE }),
    [filters, currentCursor],
  );
  const logs = useParticipantDiagnosticLogs(userId, logFilters, active);
  const deviceLabels = useMemo(() => buildDeviceLabelMap(devices), [devices]);
  const deviceOptions = useMemo(
    () => [...new Map(devices.map((device) => [device.device_id, device])).values()],
    [devices],
  );

  const acknowledgements = useMemo(
    () => getAcknowledgements(currentConfig, logs.data?.data ?? [], deviceLabels),
    [currentConfig, logs.data?.data, deviceLabels],
  );
  const acknowledgementsByDevice = useMemo(
    () => new Map(acknowledgements.map((acknowledgement) => [acknowledgement.deviceId, acknowledgement])),
    [acknowledgements],
  );
  const effectiveEnabled = getEffectiveEnabled(currentConfig);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = eventNameDraft.trim().toUpperCase() || undefined;
      if (filters.event_name !== normalized) {
        setFilters((previous) => ({ ...previous, event_name: normalized }));
        setPageCursors([undefined]);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [eventNameDraft, filters.event_name]);

  const configUpdatedAt = currentConfig?.updated_at ?? null;
  const lastConfigUpdatedAt = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastConfigUpdatedAt.current === undefined) {
      lastConfigUpdatedAt.current = configUpdatedAt;
      return;
    }
    if (configUpdatedAt !== lastConfigUpdatedAt.current) {
      lastConfigUpdatedAt.current = configUpdatedAt;
      setDurationOverride(null);
    }
  }, [configUpdatedAt]);

  function updateFilter<Key extends keyof FilterState>(key: Key, value: FilterState[Key]) {
    setFilters((previous) => ({ ...previous, [key]: value || undefined }));
    setPageCursors([undefined]);
  }

  async function saveConfig(nextEnabled: boolean, nextDuration: DiagnosticDurationHours) {
    setActionError(null);
    setEnabledOverride(nextEnabled);
    setDurationOverride(nextDuration);
    try {
      await updateConfig.mutateAsync({
        enabled: nextEnabled,
        duration_hours: nextDuration,
      });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Failed to update diagnostics");
    } finally {
      setEnabledOverride(null);
      setDurationOverride(null);
    }
  }

  async function handleExport(format: "json" | "csv") {
    setExportError(null);
    setExportingFormat(format);
    try {
      await downloadParticipantDiagnosticLogs(userId, filters, format);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Export failed");
    } finally {
      setExportingFormat(null);
    }
  }

  const status = getConfigStatus(currentConfig);

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Diagnostics</CardTitle>
            <p className="text-sm text-muted-foreground">
              Participant-wide collection for current and future devices. Retained on the server for 30 days.
            </p>
          </div>
          <Badge variant={status.variant}>{status.label}</Badge>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 border-b pb-5 sm:grid-cols-[minmax(0,1fr)_220px_auto] sm:items-end">
            <label className="flex min-h-9 items-center gap-3 text-sm font-medium">
              <input
                type="checkbox"
                role="switch"
                checked={enabledDraft}
                onChange={(event) => void saveConfig(event.target.checked, duration)}
                disabled={config.isLoading || updateConfig.isPending}
                className="size-4 accent-primary"
              />
              <span>
                <span className="block">Collect diagnostics</span>
                <span className="block text-xs font-normal text-muted-foreground">
                  {enabledDraft ? "New events will be collected until expiration." : "No new events will be collected."}
                </span>
              </span>
            </label>
            <label className="space-y-1 text-sm font-medium">
              <span>Duration</span>
              <Select
                value={String(duration)}
                onValueChange={(value) => {
                  const nextDuration = Number(value) as DiagnosticDurationHours;
                  setDurationOverride(nextDuration);
                  if (enabledDraft) void saveConfig(true, nextDuration);
                }}
                disabled={config.isLoading || updateConfig.isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {durationOverride !== null && !enabledDraft && (
                <span className="text-xs font-normal text-muted-foreground">
                  Applies when collection is enabled
                </span>
              )}
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => void config.refetch()}
              disabled={config.isFetching || updateConfig.isPending}
              aria-label="Refresh diagnostic status"
            >
              <RefreshCw className={config.isFetching ? "size-4 animate-spin" : "size-4"} />
              Refresh
            </Button>
          </div>

          {config.isError && (
            <div className="text-sm text-destructive">
              {config.error instanceof Error ? config.error.message : "Failed to load diagnostic configuration"}
            </div>
          )}
          {actionError && <div className="text-sm text-destructive">{actionError}</div>}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatusItem
              icon={<ShieldCheck className="size-4" />}
              label="Effective state"
              value={effectiveEnabled ? "Collection active" : "Collection inactive"}
              detail={currentConfig?.expires_at && isExpired(currentConfig.expires_at) ? "Local expiration reached" : undefined}
            />
            <StatusItem
              icon={<Clock3 className="size-4" />}
              label="Expiration"
              value={currentConfig?.expires_at ? formatDate(currentConfig.expires_at) : "Not scheduled"}
              detail={currentConfig?.expires_at ? expirationRelativeTime(currentConfig.expires_at) : undefined}
            />
            <StatusItem
              icon={<ShieldCheck className="size-4" />}
              label="Requested by"
              value={currentConfig?.updated_by || "Not available"}
              detail={currentConfig?.updated_at ? `Updated ${formatDate(currentConfig.updated_at)}` : undefined}
            />
            <StatusItem
              icon={<Smartphone className="size-4" />}
              label="Device acknowledgments"
              value={acknowledgements.length ? `${acknowledgements.length} device${acknowledgements.length === 1 ? "" : "s"}` : "None yet"}
              detail={acknowledgements[0] ? `${acknowledgements[0].label} · ${relativeTime(acknowledgements[0].at)}` : "Waiting for CONFIG_APPLIED"}
            />
          </div>

          {currentConfig?.enabled && !effectiveEnabled && (
            <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              Diagnostics are enabled in the requested configuration, but the expiration has passed. Devices will stop creating new events until an admin renews the request.
            </div>
          )}
          {currentConfig?.enabled && effectiveEnabled && acknowledgements.length === 0 && (
            <div className="border-l-2 border-blue-400 bg-blue-50 px-3 py-2 text-sm text-blue-950">
              Enable requested. The setting is active on the server; wait for each device to report CONFIG_APPLIED.
            </div>
          )}
          {currentConfig?.enabled && effectiveEnabled && acknowledgements.length > 0 && (
            <div className="border-l-2 border-emerald-500 bg-emerald-50 px-3 py-2 text-sm text-emerald-950">
              Device acknowledgments are evidence that those devices applied the configuration. A missing acknowledgment does not establish why a device did not receive it.
            </div>
          )}

          {deviceOptions.length > 0 && currentConfig?.enabled && (
            <div className="space-y-2 border-t pt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Per-device status</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {deviceOptions.map((device) => {
                  const acknowledgement = acknowledgementsByDevice.get(device.device_id);
                  return (
                    <div key={device.device_id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
                      <span className="truncate font-medium">{deviceLabels[device.device_id] ?? device.device_id.slice(0, 8)}</span>
                      {acknowledgement ? (
                        <Badge className="shrink-0 bg-emerald-100 text-emerald-800 border-emerald-200">Applied</Badge>
                      ) : (
                        <Badge variant="secondary" className="shrink-0">Pending</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Diagnostic event timeline</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {logs.data?.count?.toLocaleString() ?? "0"} events on this page · refreshes every 30 seconds while visible
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleExport("json")}
              disabled={exportingFormat !== null}
            >
              <FileJson className="size-4" />
              {exportingFormat === "json" ? "Exporting..." : "JSON"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleExport("csv")}
              disabled={exportingFormat !== null}
            >
              <FileSpreadsheet className="size-4" />
              {exportingFormat === "csv" ? "Exporting..." : "CSV"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              <span>From</span>
              <Input type="date" value={filters.from ?? ""} onChange={(event) => updateFilter("from", event.target.value)} />
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              <span>To</span>
              <Input type="date" value={filters.to ?? ""} onChange={(event) => updateFilter("to", event.target.value)} />
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              <span>Device</span>
              <Select value={filters.device_id ?? ""} onValueChange={(value) => updateFilter("device_id", value === "__all__" ? undefined : value ?? undefined)}>
                <SelectTrigger className="w-full"><SelectValue placeholder="All devices" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All devices</SelectItem>
                  {deviceOptions.map((device) => (
                    <SelectItem key={device.device_id} value={device.device_id}>
                      {deviceLabels[device.device_id] ?? device.device_id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              <span>Category</span>
              <Select value={filters.category ?? ""} onValueChange={(value) => updateFilter("category", value === "__all__" ? undefined : value as DiagnosticCategory)}>
                <SelectTrigger className="w-full"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {CATEGORIES.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              <span>Level</span>
              <Select value={filters.level ?? ""} onValueChange={(value) => updateFilter("level", value === "__all__" ? undefined : value as DiagnosticLevel)}>
                <SelectTrigger className="w-full"><SelectValue placeholder="All levels" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All levels</SelectItem>
                  {LEVELS.map((level) => <SelectItem key={level} value={level}>{level}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              <span>Event name</span>
              <Input
                placeholder="e.g. ALARM_SCHEDULED"
                value={eventNameDraft}
                onChange={(event) => setEventNameDraft(event.target.value)}
              />
            </label>
          </div>

          {exportError && <div className="text-sm text-destructive">{exportError}</div>}
          {logs.isError ? (
            <ErrorDisplay
              message={logs.error instanceof Error ? logs.error.message : "Failed to load diagnostic logs"}
              onRetry={() => void logs.refetch()}
            />
          ) : logs.isLoading ? (
            <Spinner />
          ) : (logs.data?.data.length ?? 0) === 0 ? (
            <EmptyState message="No diagnostic events match these filters" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-right">Payload</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(logs.data?.data ?? []).map((log) => (
                  <TableRow key={log.event_id}>
                    <TableCell className="text-xs text-muted-foreground" title={log.occurred_at}>
                      {formatDate(log.occurred_at)}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {deviceLabels[log.device_id] ?? log.device_id.slice(0, 8)}
                    </TableCell>
                    <TableCell><Badge variant="outline">{log.category}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{log.event_name}</TableCell>
                    <TableCell><Badge className={levelClass(log.level)}>{log.level}</Badge></TableCell>
                    <TableCell className="max-w-[360px] whitespace-normal text-sm">{log.message}</TableCell>
                    <TableCell className="text-right">
                      {log.payload && Object.keys(log.payload).length > 0 ? (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
                          <Download className="size-4" />
                          View
                        </Button>
                      ) : <span className="text-xs text-muted-foreground">None</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-muted-foreground">
              Page {currentPage + 1}{logs.data?.next_cursor ? " · more events available" : ""}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage === 0 || logs.isFetching}
                onClick={() => setPageCursors((previous) => previous.slice(0, -1))}
              >
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!logs.data?.next_cursor || logs.isFetching}
                onClick={() => {
                  const nextCursor = logs.data?.next_cursor;
                  if (nextCursor) setPageCursors((previous) => [...previous, nextCursor]);
                }}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Sheet open={selectedLog !== null} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <SheetHeader onClose={() => setSelectedLog(null)}>
          <SheetTitle>Diagnostic payload</SheetTitle>
          <SheetDescription>
            {selectedLog?.event_name ?? "Event"} · {selectedLog ? formatDate(selectedLog.occurred_at) : ""}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Device</div><div>{deviceLabels[selectedLog.device_id] ?? selectedLog.device_id.slice(0, 8)}</div></div>
                <div><div className="text-xs text-muted-foreground">Level</div><div>{selectedLog.level}</div></div>
              </div>
              <pre className="max-h-[65vh] overflow-auto rounded-lg bg-muted p-4 text-xs leading-relaxed">{JSON.stringify(selectedLog.payload ?? {}, null, 2)}</pre>
            </div>
          )}
        </SheetBody>
      </Sheet>
    </div>
  );
}

function StatusItem({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 truncate text-sm font-medium" title={value}>{value}</div>
      {detail && <div className="mt-0.5 truncate text-xs text-muted-foreground" title={detail}>{detail}</div>}
    </div>
  );
}

function inferDuration(config: DiagnosticConfig): DiagnosticDurationHours {
  if (!config.updated_at || !config.expires_at) return 48;
  const durationHours = (new Date(config.expires_at).getTime() - new Date(config.updated_at).getTime()) / 3_600_000;
  if (Math.abs(durationHours - 24) < 1) return 24;
  if (Math.abs(durationHours - 168) < 1) return 168;
  return 48;
}

function getEffectiveEnabled(config: DiagnosticConfig | undefined): boolean {
  if (!config) return false;
  if (typeof config.effective_enabled === "boolean") return config.effective_enabled;
  if (!config.enabled) return false;
  return !config.expires_at || !isExpired(config.expires_at);
}

function isExpired(iso: string): boolean {
  return new Date(iso).getTime() <= Date.now();
}

function expirationRelativeTime(iso: string): string {
  const remainingSeconds = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
  if (remainingSeconds <= 0) return `${relativeTime(iso)} (expired)`;
  if (remainingSeconds < 60) return `in ${remainingSeconds}s`;
  const minutes = Math.floor(remainingSeconds / 60);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.floor(hours / 24)}d`;
}

function getConfigStatus(config: DiagnosticConfig | undefined): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (!config) return { label: "Loading", variant: "secondary" };
  if (getEffectiveEnabled(config)) return { label: "Active", variant: "default" };
  if (config.enabled) return { label: "Expired", variant: "destructive" };
  return { label: "Disabled", variant: "outline" };
}

function getAcknowledgements(
  config: DiagnosticConfig | undefined,
  logs: DiagnosticLog[],
  deviceLabels: Record<string, string>,
): Array<{ deviceId: string; label: string; at: string }> {
  const byDevice = new Map<string, { deviceId: string; label: string; at: string }>();
  const add = (ack: DiagnosticDeviceAcknowledgement) => {
    const at = ack.acknowledged_at ?? ack.applied_at ?? ack.occurred_at ?? ack.received_at;
    if (!at || !ack.device_id) return;
    if (config?.updated_at && new Date(at).getTime() < new Date(config.updated_at).getTime()) return;
    const existing = byDevice.get(ack.device_id);
    if (!existing || new Date(at).getTime() > new Date(existing.at).getTime()) {
      byDevice.set(ack.device_id, {
        deviceId: ack.device_id,
        label: deviceLabels[ack.device_id] ?? ack.device_id.slice(0, 8),
        at,
      });
    }
  };

  for (const acknowledgement of config?.device_acknowledgments ?? []) add(acknowledgement);
  if (config?.last_device_acknowledgment) add(config.last_device_acknowledgment);
  for (const log of logs) {
    if (log.event_name === "CONFIG_APPLIED") add({ device_id: log.device_id, acknowledged_at: log.occurred_at });
  }
  return [...byDevice.values()].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

function levelClass(level: DiagnosticLevel): string {
  if (level === "ERROR") return "bg-red-100 text-red-800 border-red-200";
  if (level === "WARN") return "bg-amber-100 text-amber-800 border-amber-200";
  if (level === "DEBUG") return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-emerald-100 text-emerald-800 border-emerald-200";
}
